/**
 * NewMaintenanceRequestDialog
 * ---------------------------
 * Multi-item "New Maintenance Request" form for the hospital portal.
 *
 * Mirrors the visual + data shape of the public Backorder request form
 * (HospitalBackorderPage) so hospital users get the same UX when filing
 * either a backorder or a work order:
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Contact / Facility block (read-only — pulled from token) │
 *   │ Items[] — each row: Issue Type · Location · Description  │
 *   │   [+ Add another issue]                                  │
 *   │ Priority pill picker                                     │
 *   │ Photos (shared across the request)                       │
 *   │ Additional CC emails                                     │
 *   │ Notes                                                    │
 *   │ [Cancel]                            [Submit Request]     │
 *   └──────────────────────────────────────────────────────────┘
 *
 * On submit it POSTs to /api/hospital/maintenance-request with an
 * `items[]` array; the backend mirrors the first item back into the
 * legacy single-issue fields so the admin pipeline keeps working.
 */
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wrench,
  Plus,
  Trash2,
  Camera,
  Upload,
  Loader2,
  X,
  Mail,
  Zap,
  AlertTriangle,
  Clock,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ISSUE_TYPES = [
  { id: "water_leak", label: "Water Leak", priority: "high" },
  { id: "equipment_malfunction", label: "Equipment Malfunction", priority: "high" },
  { id: "hvac_issue", label: "HVAC Issue", priority: "normal" },
  { id: "plumbing", label: "Plumbing Issue", priority: "normal" },
  { id: "electrical", label: "Electrical Issue", priority: "high" },
  { id: "filter_replacement", label: "Filter Replacement", priority: "low" },
  { id: "preventive_maintenance", label: "Preventive Maintenance", priority: "low" },
  { id: "other", label: "Other", priority: "normal" },
];

const PRIORITY_TIERS = [
  {
    id: "normal",
    label: "Standard",
    sub: "5–10 business days",
    Icon: Clock,
    cls: "border-blue-500 bg-blue-50 text-blue-700",
    inactiveCls: "border-slate-200 bg-white text-slate-600",
  },
  {
    id: "high",
    label: "High",
    sub: "Same-day dispatch",
    Icon: AlertTriangle,
    cls: "border-amber-500 bg-amber-50 text-amber-700",
    inactiveCls: "border-slate-200 bg-white text-slate-600",
  },
  {
    id: "emergency",
    label: "Emergency",
    sub: "Page on-call now",
    Icon: Zap,
    cls: "border-red-500 bg-red-50 text-red-700",
    inactiveCls: "border-slate-200 bg-white text-slate-600",
  },
];

const blankItem = () => ({
  request_type: "",
  location_details: "",
  description: "",
  notes: "",
});

export default function NewMaintenanceRequestDialog({
  open,
  onOpenChange,
  user,
  healthSystem,
  facilities = [],
  onSubmitted,
}) {
  const [items, setItems] = useState([blankItem()]);
  const [priority, setPriority] = useState("normal");
  const [facilityId, setFacilityId] = useState(user?.facility_id || "");
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const reset = () => {
    setItems([blankItem()]);
    setPriority("normal");
    setFacilityId(user?.facility_id || "");
    setPhotos([]);
    setAdditionalEmails([]);
    setEmailInput("");
  };

  const handleAddItem = () => setItems((prev) => [...prev, blankItem()]);

  const handleRemoveItem = (idx) =>
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const handleUpdateItem = (idx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handlePickIssueType = (idx, typeId) => {
    handleUpdateItem(idx, "request_type", typeId);
    // If this is the first issue and the user hasn't manually overridden the
    // request-level priority, let the issue type seed it.
    if (idx === 0) {
      const t = ISSUE_TYPES.find((i) => i.id === typeId);
      if (t?.priority) setPriority(t.priority);
    }
  };

  const handleAddEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (additionalEmails.includes(trimmed)) return;
    setAdditionalEmails((prev) => [...prev, trimmed]);
    setEmailInput("");
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/heic"];
    if (!allowed.includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, WebP, or HEIC image");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Max 10MB.");
      return;
    }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/maintenance/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("hospital_token")}`,
        },
      });
      if (response.data.url) {
        setPhotos((prev) => [...prev, response.data.url]);
        toast.success("Photo uploaded");
      }
    } catch (err) {
      console.error("Photo upload error", err);
      toast.error("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanItems = items
      .map((i) => ({
        request_type: i.request_type,
        location_details: i.location_details,
        description: i.description.trim(),
        notes: i.notes,
      }))
      .filter((i) => i.request_type && i.description);

    if (cleanItems.length === 0) {
      toast.error("Add at least one issue with type + description");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("hospital_token");
      const facility = facilities.find((f) => f.id === (facilityId || user?.facility_id));
      const payload = {
        type: "maintenance",
        items: cleanItems,
        request_type: cleanItems[0].request_type,
        description: cleanItems[0].description,
        location_details: cleanItems[0].location_details,
        priority,
        facility_id: facilityId || user?.facility_id,
        facility_name: facility?.name,
        health_system_id: healthSystem?.id,
        health_system_name: healthSystem?.name,
        submitted_by: user?.name,
        submitted_by_email: user?.email,
        contact_phone: user?.phone,
        contact_name: user?.name,
        additional_emails: additionalEmails,
        photos,
      };

      await axios.post(`${API}/hospital/maintenance-request`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(
        cleanItems.length > 1
          ? `Submitted — ${cleanItems.length} issues filed`
          : "Maintenance request submitted"
      );
      reset();
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="new-maintenance-request-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Wrench className="w-5 h-5 text-blue-600" />
            New Maintenance Request
          </DialogTitle>
          <DialogDescription>
            File one or more issues in a single ticket. Each row becomes a
            tracked line item the admin team can dispatch separately.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          {/* Facility selector — only if hospital has multiple facilities */}
          {facilities.length > 1 && (
            <div className="bg-slate-50 rounded-xl p-4">
              <Label className="text-sm font-semibold text-slate-800">Facility</Label>
              <Select value={facilityId || ""} onValueChange={setFacilityId}>
                <SelectTrigger className="mt-2 bg-white" data-testid="maint-facility-select">
                  <SelectValue placeholder="Choose facility" />
                </SelectTrigger>
                <SelectContent>
                  {facilities.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Items list */}
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Issues</h3>
                <p className="text-xs text-slate-500">
                  Add a row for each problem at the facility
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddItem}
                data-testid="maint-add-item-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add issue
              </Button>
            </div>

            <div className="space-y-3">
              {items.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-slate-200 rounded-lg p-4 space-y-3"
                  data-testid={`maint-item-row-${idx}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="inline-flex w-6 h-6 rounded-full bg-blue-50 text-blue-700 text-xs font-bold items-center justify-center flex-shrink-0">
                      {idx + 1}
                    </span>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(idx)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-md"
                        data-testid={`maint-remove-item-${idx}`}
                        aria-label="Remove issue"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-slate-600">
                        Issue type *
                      </Label>
                      <Select
                        value={item.request_type}
                        onValueChange={(v) => handlePickIssueType(idx, v)}
                      >
                        <SelectTrigger
                          className="mt-1"
                          data-testid={`maint-item-type-${idx}`}
                        >
                          <SelectValue placeholder="Choose…" />
                        </SelectTrigger>
                        <SelectContent>
                          {ISSUE_TYPES.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs font-medium text-slate-600">
                        Room / location
                      </Label>
                      <Input
                        value={item.location_details}
                        onChange={(e) =>
                          handleUpdateItem(idx, "location_details", e.target.value)
                        }
                        placeholder="e.g. OR-3, Bldg A floor 2"
                        className="mt-1"
                        data-testid={`maint-item-location-${idx}`}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-medium text-slate-600">
                      What's happening? *
                    </Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) =>
                        handleUpdateItem(idx, "description", e.target.value)
                      }
                      placeholder="Describe the issue in your own words…"
                      rows={2}
                      className="mt-1"
                      data-testid={`maint-item-description-${idx}`}
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-medium text-slate-600">
                      Notes (access info, hazards, etc.)
                    </Label>
                    <Input
                      value={item.notes}
                      onChange={(e) => handleUpdateItem(idx, "notes", e.target.value)}
                      placeholder="Optional"
                      className="mt-1"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="text-base font-semibold text-slate-900 mb-1">
              Response Tier
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              How urgently does the team need to respond?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {PRIORITY_TIERS.map((tier) => {
                const active = priority === tier.id;
                const Icon = tier.Icon;
                return (
                  <button
                    type="button"
                    key={tier.id}
                    onClick={() => setPriority(tier.id)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      active ? tier.cls : tier.inactiveCls
                    }`}
                    data-testid={`maint-priority-${tier.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span className="font-semibold text-sm">{tier.label}</span>
                    </div>
                    <p className="text-xs mt-1 opacity-80">{tier.sub}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Photos */}
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Photos</h3>
                <p className="text-xs text-slate-500">
                  Optional — speeds up dispatch by 50%
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                />
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  data-testid="maint-camera-btn"
                >
                  <Camera className="w-4 h-4 mr-1" /> Camera
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  data-testid="maint-upload-btn"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-1" />
                  )}
                  Upload
                </Button>
              </div>
            </div>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                {photos.map((url, i) => (
                  <div key={i} className="relative group rounded-lg overflow-hidden border border-slate-200">
                    <img src={url} alt="" className="w-full h-20 object-cover" />
                    <button
                      type="button"
                      onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                      aria-label="Remove photo"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* CC additional emails */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="text-base font-semibold text-slate-900 mb-1">
              CC additional contacts
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              They'll receive every status update on this ticket.
            </p>
            <div className="flex gap-2">
              <Input
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddEmail();
                  }
                }}
                placeholder="biomed@hospital.org"
                data-testid="maint-cc-email-input"
              />
              <Button type="button" variant="outline" onClick={handleAddEmail}>
                <Mail className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {additionalEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {additionalEmails.map((e) => (
                  <span
                    key={e}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full"
                  >
                    {e}
                    <button
                      type="button"
                      onClick={() =>
                        setAdditionalEmails((prev) => prev.filter((x) => x !== e))
                      }
                      className="hover:text-blue-900"
                      aria-label="Remove email"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="maint-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700"
              disabled={submitting}
              data-testid="maint-submit-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wrench className="w-4 h-4 mr-2" />
              )}
              Submit request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
